// domTimeHeaderSticky2.tsx
import { CalendarGroupingEnum, CalendarSpanEnum } from "src/types";
import moment from "moment";

export const domTimeHeaderSticky2 = (
  instanceKey: string,
  span: CalendarSpanEnum,
  grouping: CalendarGroupingEnum,
  currentDate: Date,
  events: any[],
  isMobile: boolean,
  calendarIndex?: number,
): (() => void) | undefined => {
  let timerId: number | undefined;

  // Store references to modified elements for cleanup
  const modifiedElements: HTMLElement[] = [];
  const originalStyles: Map<HTMLElement, string> = new Map();
  
  // Store event listeners for cleanup
  const eventListeners: Array<{
    element: HTMLElement;
    type: string;
    listener: EventListener;
  }> = [];

  if (grouping === "individual" && (span === "day" || span === "week")) {
    timerId = window.setTimeout(() => {
      const calendarContainer = document.querySelector(`#allCalendarElements2-1`);
      if (!calendarContainer) return;
      
      // Find the time view element
      const timeView = calendarContainer.querySelector('.rbc-time-view');
      if (!timeView) return;
      
      // Check if we've already processed this (to avoid duplicates)
      if (timeView.querySelector('.rbc-time-header-clone')) return;
      
      const timeHeader = calendarContainer.querySelector('.rbc-time-header') as HTMLElement;
      
      // Only proceed if time header exists
      if (timeHeader) {
        // Clone the timeHeader
        const clonedHeader = timeHeader.cloneNode(true) as HTMLElement;
        
        // Add a class to identify the clone
        clonedHeader.classList.add('rbc-time-header-clone');

        // Hide original names from the original header (store original display values)
        const originalNameCells = timeHeader.querySelectorAll('.rbc-row.rbc-time-header-cell') as NodeListOf<HTMLElement>;
        originalNameCells.forEach(cell => {
          // Store original display value for cleanup
          originalStyles.set(cell, cell.style.display);
          cell.style.display = 'none';
          modifiedElements.push(cell);
        });

        // Remove all day cells from cloned header so that we have names only
        const allDayCells = clonedHeader.querySelectorAll('.rbc-allday-cell') as NodeListOf<HTMLElement>;
        allDayCells.forEach(cell => {
          cell.style.display = 'none';
        });
        
        // Apply sticky positioning styles
        clonedHeader.style.position = 'sticky';
        clonedHeader.style.top = '0';
        clonedHeader.style.zIndex = '15';
        
        // Find the .rbc-time-view container
        const allCalendarElements = document.querySelector('.rbc-time-view');
        
        if (allCalendarElements) {
          // Insert the cloned header as the first child
          allCalendarElements.insertBefore(clonedHeader, allCalendarElements.firstChild);
          
          // Store reference to cloned header for cleanup
          modifiedElements.push(clonedHeader);
        }

        // Find all plus buttons in the cloned header
        const plusButtons = clonedHeader.querySelectorAll('button');
        plusButtons.forEach((button) => {
          // Check if this is a plus button (by its content or style)
          if (button.textContent === '+' || button.innerHTML === '+') {
            const clickHandler = (e: Event) => {
              e.stopPropagation();
              
              // Find the date from the parent header cell
              const headerCell = button.closest('.rbc-header-cell') || 
                               button.closest('[class*="rbc-header"]') || 
                               button.parentElement;
              
              // Try to find the date text in the header cell
              const dateText = headerCell?.textContent?.replace('+', '').trim() || '';
              
              let date;
              if (dateText) {
                // Parse the date from the header text
                const dateMatch = dateText.match(/(\d{1,2})\/(\d{1,2})/);
                if (dateMatch) {
                  const month = parseInt(dateMatch[1]) - 1; // JavaScript months are 0-indexed
                  const day = parseInt(dateMatch[2]);
                  date = new Date(currentDate.getFullYear(), month, day);
                  
                  // Handle year boundary
                  if (month > currentDate.getMonth() && currentDate.getMonth() < 3) {
                    date.setFullYear(currentDate.getFullYear() - 1);
                  } else if (month < currentDate.getMonth() && currentDate.getMonth() > 9) {
                    date.setFullYear(currentDate.getFullYear() + 1);
                  }
                }
              }
              
              // If we couldn't parse the date, try getting it from column index
              if (!date && headerCell) {
                const allHeaders = clonedHeader.querySelectorAll('.rbc-header-cell, [class*="rbc-header"]');
                const columnIndex = Array.from(allHeaders).indexOf(headerCell as Element);
                
                if (columnIndex >= 0 && span === 'week') {
                  // For week view, calculate based on the start of the week
                  date = new Date(currentDate);
                  const dayOfWeek = date.getDay();
                  date.setDate(date.getDate() - dayOfWeek + columnIndex);
                } else if (span === 'day') {
                  // For day view, use the current date
                  date = new Date(currentDate);
                }
              }
              
              // If still no date, use current date as fallback
              if (!date) {
                date = new Date(currentDate);
              }
              
              // Dispatch custom event
              const customEvent = new CustomEvent('individualHeaderAddClick', {
                detail: {
                  instanceKey: instanceKey,
                  date: date,
                  resourceId: null
                }
              });
              document.dispatchEvent(customEvent);
            };
            
            // Add event listener and store reference for cleanup
            button.addEventListener('click', clickHandler);
            eventListeners.push({
              element: button,
              type: 'click',
              listener: clickHandler
            });
          }
        });
      }
    }, 150);

    // Return cleanup function
    return () => {
      // Clear the timeout if it exists
      if (timerId) {
        clearTimeout(timerId);
      }

      // Remove all event listeners
      eventListeners.forEach(({ element, type, listener }) => {
        element.removeEventListener(type, listener);
      });
      eventListeners.length = 0;

      // Remove the cloned header if it exists
      const clonedHeader = document.querySelector('.rbc-time-header-clone');
      if (clonedHeader) {
        clonedHeader.remove();
      }

      // Restore original styles for modified elements
      modifiedElements.forEach(element => {
        // Skip if element is the cloned header (already removed)
        if (element.classList?.contains('rbc-time-header-clone')) {
          return;
        }
        
        const originalDisplay = originalStyles.get(element);
        if (originalDisplay !== undefined) {
          element.style.display = originalDisplay;
        } else {
          // If no original style was stored, remove the display property
          element.style.removeProperty('display');
        }
      });

      // Clear the stored references
      modifiedElements.length = 0;
      originalStyles.clear();
    };
  }
  
  return undefined;
};